import { splitProps, createMemo } from "solid-js";

/**
 * @typedef {object} UIAlertProps
 * @property {'info' | 'success' | 'warning' | 'error'} [variant] - Severity variant
 * @property {boolean} [soft=false] - Use the softer DaisyUI alert treatment
 * @property {string} [role] - ARIA role, e.g. 'status' or 'alert'
 * @property {import('solid-js').JSX.Element} children - Alert content
 * @property {string} [className] - Additional CSS classes
 */

/**
 * An inline alert banner with severity variants, mirroring the DaisyUI alert
 * conventions used across the app.
 * @param {UIAlertProps & import('solid-js').JSX.HTMLAttributes<HTMLDivElement>} props The component props
 * @returns {import('solid-js').JSX.Element} The alert banner
 */
export function UIAlert(props) {
  const [local, others] = splitProps(props, [
    "variant",
    "soft",
    "children",
    "className",
  ]);

  const variantClasses = {
    info: "tw:d-alert-info",
    success: "tw:d-alert-success",
    warning: "tw:d-alert-warning",
    error: "tw:d-alert-error",
  };

  const classes = createMemo(() => {
    return [
      "tw:d-alert",
      "tw:text-dense",
      local.soft ? "tw:d-alert-soft" : "",
      local.variant ? variantClasses[local.variant] : "",
      local.className || "",
    ]
      .filter(Boolean)
      .join(" ");
  });

  return (
    <div className={classes()} {...others}>
      {local.children}
    </div>
  );
}
